import { Injectable } from '@angular/core';
import { ProductResponse } from './api.service';
import { LanguageService } from './language.service';
import { MenuSupplement } from '../models/supplement.model';

const MENU_PRODUCTS_KEY = 'menuProducts';
const FEATURED_PRODUCTS_KEY = 'featuredProducts';
const CLIENT_DATA_KEY = 'clientData';

/**
 * The venue's menu, indexed by product id and by add-on id.
 *
 * The web analogue of iOS `FeatureMenuContainer`'s product/supplement catalogue, but sourced from
 * the `menuProducts` / `featuredProducts` sessionStorage caches rather than from a network
 * callback: `MenuComponent.processMenuResponse` already writes the full flattened product list
 * there (subcategories included) on every menu load, and everything else in this app reads its
 * product data back out of it. Indexing the same caches means there is no second registration path
 * to forget to call — an unregistered slice is exactly how iOS ended up with add-ons stuck on a
 * frozen name.
 *
 * Two things need this index that a cart line cannot answer on its own:
 *  - a cart line carries add-on ids and a NAME SNAPSHOT taken when the guest ticked the box; the
 *    localized meta rows live only on the menu entry, so a name that must follow a language switch
 *    has to be re-resolved here;
 *  - a cart line carries a product id but not the product, so opening the product sheet on a cart
 *    line resolves it here.
 */
@Injectable({ providedIn: 'root' })
export class SupplementCatalogService {
  private products = new Map<number, ProductResponse>();
  private supplements = new Map<number, MenuSupplement>();
  private supplementsSupported = false;

  // The raw strings the current index was built from. sessionStorage is the source of truth and
  // the menu component rewrites it on every load, so the index is rebuilt from a cheap string
  // comparison instead of anyone remembering to invalidate it.
  private indexedMenuRaw: string | null = null;
  private indexedFeaturedRaw: string | null = null;
  private indexedClientRaw: string | null = null;
  private lastReadAt = 0;

  // These lookups are called from template bindings, i.e. potentially once per tile per change
  // detection pass, and reading a few hundred KB out of sessionStorage that often is a jank
  // source. One re-read per second is well inside a menu load (which takes a network round trip)
  // and matches the polling interval the tile components already use for clientData.
  private readonly rereadIntervalMs = 1000;

  constructor(private languageService: LanguageService) {}

  /** This venue's menu entry for an add-on, or undefined before the menu has loaded. */
  supplement(id: number): MenuSupplement | undefined {
    this.ensureIndex();
    return this.supplements.get(id);
  }

  /**
   * This venue's menu entry for a product, or undefined before the menu has loaded (a cart
   * restored from localStorage on a cold open) or for a product that has since left the menu.
   */
  product(id: number): ProductResponse | undefined {
    this.ensureIndex();
    return this.products.get(id);
  }

  /**
   * The add-on's name in the current language, re-resolved on every read.
   *
   * `fallback` is the snapshot stored on the cart line, and it is only a fallback: it was
   * localized once, when the guest ticked the box, so it follows neither a language switch nor a
   * rename. It still has to exist for the two cases the catalogue cannot answer — the menu not
   * loaded yet, and an add-on that has since left the menu.
   *
   * Goes through `LanguageService.localizeMetaValue` rather than re-deriving the slug rules, which
   * is where the English fallback and the legacy code aliases already live.
   */
  localizedSupplementName(id: number, fallback: string): string {
    const entry = this.supplement(id);
    if (!entry) return fallback;
    const base = (entry.name ?? '').trim();
    const localized = this.languageService.localizeMetaValue(entry.meta_data, '', base || fallback);
    return localized.trim() || fallback;
  }

  /**
   * Whether this venue offers menu add-ons at all.
   *
   * The backend has already folded the platform switch, the POS type (add-ons are Hype-only) and
   * the per-venue column into this one flag, so there is nothing to re-derive. It gates whether the
   * picker is DISPLAYED; whether it is SELECTABLE is the existing ordering-enabled check, because a
   * browse-only menu should still say that "Extra cheese +50" exists.
   */
  isSupplementsSupported(): boolean {
    this.ensureIndex();
    return this.supplementsSupported;
  }

  /**
   * Whether this product offers add-ons a guest can actually choose.
   *
   * ONE expression, used by the tile, the sheet and the tile router alike. iOS shipped this branch
   * twice and the two copies disagreed on a stale cached menu: the tile decided the product had
   * add-ons and opened a sheet whose picker had decided it did not, leaving the guest on a screen
   * with no way to add anything.
   */
  productHasSupplements(product: ProductResponse | null | undefined): boolean {
    return this.isSupplementsSupported() && (product?.supplements?.length ?? 0) > 0;
  }

  /**
   * Drop the cached index so the next lookup re-reads sessionStorage.
   *
   * Only needed to beat the re-read interval — e.g. straight after a menu load, when a tile may
   * render in the same tick that wrote the cache.
   */
  refresh(): void {
    this.ensureIndex(true);
  }

  private ensureIndex(force = false): void {
    const now = Date.now();
    if (!force && this.lastReadAt !== 0 && now - this.lastReadAt < this.rereadIntervalMs) return;
    this.lastReadAt = now;

    const menuRaw = sessionStorage.getItem(MENU_PRODUCTS_KEY);
    const featuredRaw = sessionStorage.getItem(FEATURED_PRODUCTS_KEY);
    const clientRaw = sessionStorage.getItem(CLIENT_DATA_KEY);

    if (!force &&
        menuRaw === this.indexedMenuRaw &&
        featuredRaw === this.indexedFeaturedRaw &&
        clientRaw === this.indexedClientRaw) {
      return;
    }

    this.indexedMenuRaw = menuRaw;
    this.indexedFeaturedRaw = featuredRaw;
    this.indexedClientRaw = clientRaw;

    this.products.clear();
    this.supplements.clear();
    // Featured products are indexed last so a later payload wins, which is also how iOS
    // `registerMenuProducts` resolves a repeat: a renamed add-on takes effect on the next load.
    this.indexProducts(this.parseProducts(menuRaw));
    this.indexProducts(this.parseProducts(featuredRaw));
    this.supplementsSupported = this.parseSupplementsSupported(clientRaw);
  }

  private indexProducts(products: ProductResponse[]): void {
    for (const product of products) {
      if (product?.id != null) {
        this.products.set(product.id, product);
      }
      for (const supplement of product?.supplements ?? []) {
        if (supplement?.id != null) {
          this.supplements.set(supplement.id, supplement);
        }
      }
    }
  }

  // A corrupt cache must degrade to "this venue has no add-ons", never to a thrown error: the
  // caller is a tile deciding whether to draw a chip, and the menu behind it is still perfectly
  // orderable without one.
  private parseProducts(raw: string | null): ProductResponse[] {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as ProductResponse[]) : [];
    } catch (error) {
      console.error('Error parsing cached products for supplement catalog:', error);
      return [];
    }
  }

  private parseSupplementsSupported(raw: string | null): boolean {
    if (!raw) return false;
    try {
      const clientData = JSON.parse(raw);
      // Strict true: a venue whose payload predates the flag must read as "no add-ons", not as
      // "add-ons with an empty catalogue".
      return clientData?.is_supplements_supported === true;
    } catch (error) {
      console.error('Error parsing clientData for supplement catalog:', error);
      return false;
    }
  }
}
